import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useCRM } from '../../contexts/CRMContext';
import crmApi from '../../utils/crmApi';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const TrialStatusBanner = () => {
  const { subscription } = useSubscription();
  const { currentAgency, user, loading: crmLoading } = useCRM();
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated
  const isAuthenticated = !!user && !crmLoading;
  const token = localStorage.getItem('crm_token');

  useEffect(() => {
    // Only load subscription status if user is authenticated and has an agency context
    if (!isAuthenticated || !token || !currentAgency?._id) {
      setLoading(false);
      return;
    }

    loadSubscriptionStatus();
    // Refresh every hour
    const interval = setInterval(loadSubscriptionStatus, 3600000);
    return () => clearInterval(interval);
  }, [currentAgency, isAuthenticated, token]);

  const loadSubscriptionStatus = async () => {
    // Don't make API call if not authenticated or no agency context
    if (!isAuthenticated || !token || !currentAgency?._id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await crmApi.get('/subscription/status');
      if (res.data?.success) {
        setSubscriptionStatus(res.data.subscription);
      }
    } catch (error) {
      // Silently handle expected errors (401 unauthenticated, 400/404 missing subscription context)
      const status = error.response?.status;
      if (status !== 401 && status !== 400 && status !== 404) {
        console.error('Failed to load subscription status:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Don't render if not authenticated or still loading
  if (!isAuthenticated || !token || loading || !subscriptionStatus) {
    return null;
  }

  const { 
    isTrialActive, 
    trialDaysRemaining, 
    needsPayment, 
    isExpired,
    planId 
  } = subscriptionStatus;

  // Don't show banner if not on trial or if already subscribed
  if (!isTrialActive && planId !== 'trial' && !needsPayment) {
    return null;
  }

  // Show warning if trial is ending soon (less than 3 days)
  const isTrialEndingSoon = isTrialActive && trialDaysRemaining !== null && trialDaysRemaining <= 3;
  const isTrialExpired = !isTrialActive && planId === 'trial';

  if (isTrialExpired || isExpired) {
    return (
      <Card className="m-4 p-4 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            <div>
              <p className="font-semibold text-red-900 dark:text-red-100">
                Trial Expired
              </p>
              <p className="text-sm text-red-700 dark:text-red-300">
                Your trial has ended. Subscribe to continue using the platform.
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/subscription')}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Subscribe Now
            <ArrowRightIcon className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </Card>
    );
  }

  if (isTrialEndingSoon) {
    return (
      <Card className="m-4 p-4 bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="font-semibold text-yellow-900 dark:text-yellow-100">
                Trial Ending Soon
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Only {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} left in your trial
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/subscription')}
            variant="outline"
            className="border-yellow-600 text-yellow-700 hover:bg-yellow-100"
          >
            Choose Plan
            <ArrowRightIcon className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </Card>
    );
  }

  if (isTrialActive && trialDaysRemaining !== null) {
    return (
      <Card className="m-4 p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClockIcon className="h-5 w-5 text-blue-600" />
            <div>
              <p className="font-semibold text-blue-900 dark:text-blue-100">
                Free Trial Active
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-blue-100 text-blue-800">
              {trialDaysRemaining} days left
            </Badge>
            <Button
              onClick={() => navigate('/subscription')}
              variant="outline"
              size="sm"
            >
              View Plans
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return null;
};

export default TrialStatusBanner;

